import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { toolRegistry } from './tools/registry';
import AppLayout from './components/layout/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './components/ThemeProvider';
import { SEO } from './components/SEO';

function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: string }).message;
  const name = (error as { name?: string }).name;
  return (
    message?.includes('Failed to fetch dynamically imported module') ||
    message?.includes('Loading chunk') ||
    name === 'ChunkLoadError'
  ) ?? false;
}

function App() {
  // Global handler for chunk load errors that occur outside React's error boundary
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        event.preventDefault();
        // Navigate to current URL to fetch fresh assets (similar to Vue Router's onError approach)
        window.location.href = window.location.href;
      }
    };

    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, []);

  return (
    <ThemeProvider defaultTheme="system">
      <BrowserRouter>
        <SEO />
        <ErrorBoundary>
          <AppLayout>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              }
            >
              <Routes>
                {toolRegistry.map((tool) => {
                  const Component = tool.component;
                  return <Route key={tool.id} path={tool.path} element={<Component />} />;
                })}
                {/* 404 fallback */}
                <Route
                  path="*"
                  element={
                    <div className="container mx-auto py-8">
                      <h1 className="text-4xl font-bold">404 - Not Found</h1>
                      <p className="mt-2 text-muted-foreground">
                        The page you're looking for doesn't exist.
                      </p>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </AppLayout>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
